/**
 *
 * General Entity container class
 * Cannot be used directly, extend with
 * particular entity implementation
 *
 *
 */

import { Component } from "react";

import Entity from "../datasource/Entity";
import PropTypes from "prop-types";
import ReactRouterPropTypes from "react-router-prop-types";
import { SEARCH_FIELDS } from "../lib/data";

class Container extends Component {
    constructor(props) {
        super(props);
        this.state = {
            AllItems: [],
            currentItem: null,
            error: null,
            loading: false,
            AuxItems: {},
        };
        this.globalSearch = {
            search: SEARCH_FIELDS,
        };
    }

    setGlobalSearch = (newSearch) => {
        this.globalSearch = newSearch;
    };

    updateGlobalSearch = (searchParamObj, searchEntity) => {
        const updatedGlobalSearch = { ...this.globalSearch };
        if (updatedGlobalSearch?.search) {
            updatedGlobalSearch.search[searchEntity] = searchParamObj;
            this.globalSearch = updatedGlobalSearch;
        }
    };

    /**
     *
     * After component is mounted, run initDatasource
     * to attach required entity API source
     *
     * e.g. this.initDatasource("hotels")
     *
     * @param {string} modelName    Entity name, one of /datasource/schema/*.json
     *
     */
    initDatasource = (modelName) => {
        this.modelName = modelName;
        const { mainId } = { ...this.props.match.params };
        const { baseEntity } = { ...this.props };

        this.datasource = this.props.Entity
            ? this.props.Entity
            : new Entity(modelName, baseEntity, mainId);
    };

    /**
     *
     * Featch auxilary data from another sibling entity (bound to current Base entity)
     * E.g. fetch list of Hotel's room types to render in Hotel's quotas grid
     *
     * Fetched items are available in auxData prop.
     *
     */
    getAuxData = async () => {
        const { mainId } = { ...this.props.match.params };
        let { auxEntity, auxSource } = { ...this.props };
        if (!Array.isArray(auxEntity)) {
            auxEntity = [auxEntity];
        }
        for (const entity of auxEntity) {
            await auxSource(mainId, entity);
        }
    };

    /**
     *
     * Sets Loading state to true
     *
     */

    setLoading = (callback) =>
        this.setState(
            (state) => ({
                ...state,
                loading: true,
            }),
            callback,
        );

    /**
     *
     * Sets Loading state to false
     *
     */

    setReady = (callback) =>
        this.setState(
            (state) => ({
                ...state,
                loading: false,
            }),
            callback,
        );

    /**
     *
     * If forbidden error, returns true to redirect to "/"
     *
     */

    checkAvailability = () => {
        const forbidCode = 403;

        if (this.state.error) {
            if (this.state.error.status === forbidCode) {
                return true;
            }
        }
    };

    /**
     *
     * Datasource interaction method
     * Retrieves list of entities  and stores
     * it in this.state.AllItems
     *
     */

    getAllItems = (params, object) => {
        this.setLoading(() =>
            this.datasource
                .getAll(params, object)
                .then((AllItems) => {
                    this.setState((state) => ({
                        ...state,
                        AllItems,
                        currentItem: null,
                        loading: false,
                        error: null,
                    }));
                })
                .catch((error) => this.handleFetchError(error.response)),
        );
    };

    /**
     *
     * Datasource interaction method
     * Performs bulk update of entities
     *
     * @param {array} items
     */

    updateAllItems = (items) => {
        const itemsToUpdate = Array.isArray(items)
            ? items
            : this.state.AllItems;

        return new Promise((resolve, reject) => {
            this.setLoading(() =>
                this.datasource
                    .updateAll(itemsToUpdate)
                    .then((AllItems) => {
                        if (isGeneralErrors(AllItems)) {
                            alert(isGeneralErrors.join("\n"));
                        }

                        this.setState((state) => ({
                            ...state,
                            currentItem: null,
                            loading: false,
                            error: Array.isArray(items) ? AllItems.data : null,
                        }));

                        resolve(AllItems);
                    })
                    .catch((error) => {
                        this.handleUpdateError(error.response);
                        reject(error);
                    }),
            );
        });
    };

    /**
     *
     * Datasource interaction method
     * Retrieves list of entities bound to current entity
     * (e.g. fetches all configuration items related to current Hotel)
     * and stores it in this.state.AllItems
     *
     */

    getAllBoundItems = (id, boundEntity) =>
        this.setLoading(() =>
            this.datasource
                .relations(id, boundEntity)
                .getRelated()
                .then((AllItems) => {
                    const AuxItems = { ...this.state.AuxItems };
                    AuxItems[boundEntity] = [...AllItems];
                    this.setState((state) => ({
                        ...state,
                        AuxItems,
                        currentItem: null,
                        loading: false,
                        error: null,
                    }));
                })
                .catch((error) => this.handleFetchError(error.response)),
        );

    /**
     *
     * Creates empty item and puts it in
     * this.state.currentItem to be filled by Add New Item form
     *
     */

    initCurrentItem = () =>
        this.setState((state) => ({
            ...state,
            currentItem: { ...this.datasource.getEmptyObject() },
        }));

    /**
     *
     * Datasource interaction method
     * Retrieves particular entity by Id
     *
     * and stores it in this.state.currentItem
     *
     * @param {int} id      Entity id
     *
     */

    getItem = (id) => {
        this.setLoading(() =>
            this.datasource
                .getOne(id)
                .then((currentItem) =>
                    this.setState((state) => ({
                        ...state,
                        currentItem,
                        loading: false,
                        error: null,
                    })),
                )
                .catch((error) => this.handleFetchError(error.response)),
        );
    };

    /**
     *
     * Datasource interaction method
     * Sets the "Active" field of entity to given value
     * @param {int} id              Entity id
     * @param {boolean} status      New value for Active field
     *
     */

    toggleItem = (id, status) => {
        this.setLoading(() =>
            this.datasource
                .toggle(id, status)
                .then(() => {
                    this.getAllItems();
                })
                .catch((error) => {
                    this.handleUpdateError(error.response);
                }),
        );
    };

    /**
     *
     * Datasource interaction method
     * Removes entity
     *
     * @param {int} id      Entity id
     *
     */

    removeItem = (id, name = false) => {
        if (name && !confirm(`Вы действительно хотите удалить \n${name}?`))
            return;

        this.setLoading(() =>
            this.datasource
                .remove(id)
                .then((result) => {
                    this.getAllItems();
                })
                .catch((error) => {
                    if (
                        error.response &&
                        (error.response.status === 500 ||
                            error.response.status === 409)
                    ) {
                        alert(error.response.data.message);
                        this.getAllItems();
                    } else {
                        alert(error.response.data.message ?? "Remove error");
                    }
                }),
        );
    };

    /**
     *
     * Datasource interaction method
     * Removes relation between current entity and some other entity
     *
     * @param {int} id                  Entity id
     * @param {string} boundEntity      Other entity name
     * @param {int} relId               Other entity id
     *
     */

    removeBoundItem = (id, boundEntity, relId) => {
        this.setLoading(() =>
            this.datasource
                .relations(id, boundEntity)
                .removeRelation(relId)
                .then((result) => {
                    if (result) {
                        this.getAllBoundItems(id, boundEntity);
                    } else {
                        alert("Remove error");
                    }
                }),
        );
    };

    /**
     *
     * Hanldes datasource fetch error
     * For now just alerts an error
     *
     */

    handleFetchError = (response) => {
        alert(response.data.message);
        this.setState((state) => ({
            ...state,
            error: response,
            loading: false,
        }));
    };

    /**
     *
     * Hanldes datasource update error
     * Puts errors array into this.state.error
     *
     */

    handleUpdateError = (response) => {
        let messages = ["Ошибка обновления данных"];

        // if (isGeneralErrors(response)) {
        //     messages = [...messages, ...isGeneralErrors(response)];
        // }

        let expandedErrors = { ...response.data };

        if (response?.data?.message) {
            messages.push(response?.data?.message);
        }

        if (expandedErrors.name) {
            messages.push("Поле Наименование обязательно для заполнения.");
        }

        if (expandedErrors.code) {
            messages.push("Поле Символьный код обязательно для заполнения.");
        }

        if (expandedErrors.partial_payment_amount) {
            messages.push("Поле Частичная оплата должно быть целым числом.");
        }

        alert(messages.join("\n"));

        let error = null;
        if (response.data && typeof response.data === "object") {
            error = { ...response.data };
        }
        this.setState((state) => ({
            ...state,
            loading: false,
            error,
        }));
    };

    /**
     *
     * Hanldes datasource new item add error
     * Puts errors array into this.state.error
     *
     */

    handleAddError = (response) => {
        alert("Ошибка добавления обьекта");

        let error = null;
        if (response.data && typeof response.data === "object") {
            error = response.data;
        }
        this.setState((state) => ({
            ...state,
            loading: false,
            error,
        }));
    };

    /**
     *
     * Datasource interaction method
     * updates item that is stored in this.state.currentItem
     *
     */

    updateItem = () =>
        this.setLoading(() =>
            this.datasource
                .updateOne(this.state.currentItem)
                .then((result) => {
                    if (result.data?.attention)
                        alert(
                            "Успешно сохранено" + "\n" + result.data.attention,
                        );
                    this.props.history.push(this.datasource.routes().home);
                })
                .catch((result) => this.handleUpdateError(result.response)),
        );

    updateItemWithoutReload = () =>
        this.datasource
            .updateOne(this.state.currentItem)
            .then((result) => {
                if (result.data?.attention)
                    alert("Успешно сохранено" + "\n" + result.data.attention);
                else alert("Успешно сохранено");
            })
            .catch((result) => this.handleUpdateError(result.response));

    /**
     *
     * Datasource interaction method
     * updates item that is stored in this.state.currentItem
     *
     */

    addItem = () =>
        this.setLoading(() =>
            this.datasource
                .addOne(this.state.currentItem)
                .then((result) => {
                    if (result.data?.attention)
                        alert(
                            "Объект успешно добавлен" +
                                "\n" +
                                result.data.attention,
                        );
                    else alert("Объект успешно добавлен");
                    this.props.history.push(this.datasource.routes().home);
                })
                .catch((result) => this.handleUpdateError(result.response)),
        );

    /**
     *
     *
     * Updates field value
     * in this.state.currentItem
     * Useful as Add/Edit form callbacks
     *
     * @param {string}  fieldName
     * @param {*} fieldValue
     *
     *
     */

    setFieldValue = (fieldName, fieldValue) => {
        if (fieldName === "percent_discount") {
            if (fieldValue > 100) {
                alert("Значение скидки не может быть больше 100 процентов!");
                fieldValue = 100;
            }

            if (fieldValue < 0) {
                alert("Значение скидки не может быть меньше 0 процентов!");
                fieldValue = 0;
            }
        }

        const updatedItem = { ...this.state.currentItem };
        updatedItem[fieldName] = fieldValue;
        let updatedError = { ...this.state.error };
        if (updatedError[fieldName]) {
            delete updatedError[fieldName];
            if (Object.keys(updatedError).length === 0) {
                updatedError = null;
            }
        }

        this.setState((state) => ({
            ...state,
            currentItem: updatedItem,
            error: updatedError,
        }));
    };

    /**
     *
     *
     * Updates particular items in this.state.AllItems
     * Useful as Grid items update callbacks
     *
     * @param {int}  id
     * @param {object} fieldValue
     *
     *
     */

    setItems = (newItems) => {
        const updatedItems = [...this.state.AllItems];
        newItems.forEach((v, i) => (updatedItems[i] = { ...v }));
        this.setState((state) => ({
            ...state,
            updatedItems,
        }));
    };

    /**
     *
     * Updates particular item
     *
     * @param {object} updatedItem
     *
     */

    updateParticularItem = (updatedItem) => {
        this.datasource
            .updateOne(updatedItem)
            .then(() => {
                this.setState((state) => {
                    const updAllItems = state.AllItems.map((item) =>
                        item.id === updatedItem.id ? updatedItem : item,
                    );
                    return {
                        ...state,
                        AllItems: updAllItems,
                    };
                });
            })
            .catch((e) => {
                this.handleUpdateError(e);
            });
    };

    /**
     *
     * Updates M-M relation values of current item
     *
     * @parem
     */

    setRelationsValue = (entityName, newValue) => {
        this.setState((state) => {
            const updatedState = { ...state };
            updatedState.currentItem.relations[entityName] = newValue;
            return updatedState;
        });
    };

    /**
     *
     * Generates frontend routes
     *
     */

    getViewLink = (id) => this.datasource.routes().view(id);

    getEditLink = (id) => this.datasource.routes().edit(id);

    getBackLink = () =>
        this.props.baseEntity
            ? this.datasource.routes().base()
            : this.datasource.routes().home;

    getBoundLink = (id, entity) => this.datasource.routes().bound(id, entity);

    /**
     *
     * This generic class has no render method.
     * Extend this basic container with
     * particluar entity container implementation
     *
     */

    render = () => {
        throw new Error(
            "Extend current class with particular model and provide proper (render) implementation",
        );
    };
}
// Helper functions

/**
 * Searches 'general] property in error response object
 *
 * @param {*} response
 * @returns {array | null}
 */
const isGeneralErrors = (response) =>
    response &&
    response.data &&
    response.data.general &&
    Array.isArray(response.data.general) &&
    response.data.general.length > 0
        ? response.data.general
        : null;

Container.propTypes = {
    Entity: PropTypes.instanceOf(Entity),
    match: ReactRouterPropTypes.match.isRequired,
    history: ReactRouterPropTypes.history,
    baseEntity: PropTypes.string,
    auxEntity: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    auxData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    auxDataSource: PropTypes.func,
};

export default Container;
